import axios, { type AxiosResponse } from 'axios';
import type {
  User,
  CreateUserInput,
  UpdateUserProfileInput,
  UserPreferences,
  TimeEntry,
  CreateTimeEntryInput,
  Project,
  CreateProjectInput,
  UpdateProjectInput,
  Task,
  CreateTaskInput,
  UpdateTaskInput,
  Client,
  CreateClientInput,
  UpdateClientInput,
  Invoice,
  CreateInvoiceInput,
  UserInvitation,
  CreateInvitationInput,
  Organization,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  CurrencyInfo,
  ExchangeRate,
} from '@voltasis/shared';
import { config } from '@/config/env';
import { getIdToken, signOutUser } from './auth';
import { logger } from '@/utils/logger';
import toast from 'react-hot-toast';

function unwrap<T>(response: AxiosResponse<{ success: boolean; data: T }>): T {
  return response.data.data;
}

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (reqConfig) => {
  try {
    const token = await getIdToken();
    reqConfig.headers.Authorization = token;
  } catch {
    logger.warn('Token fetch failed — signing out');
    await signOutUser();
    return Promise.reject(new Error('Authentication failed — signed out'));
  }
  return reqConfig;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    logger.error('API request failed', { status, url, message: error.message });

    if (!error.response) {
      const networkError = new Error(
        `Network error: ${error.message} (${url ?? 'unknown URL'})`,
      );
      networkError.cause = error;
      return Promise.reject(networkError);
    }

    if (status === 401) {
      logger.warn('Received 401 — signing out');
      await signOutUser();
    }

    if (status === 403) {
      const responseData = error.response?.data as Record<string, unknown> | undefined;
      if (responseData?.code === 'UPGRADE_REQUIRED') {
        const message = typeof responseData.message === 'string'
          ? responseData.message
          : 'This feature requires a Pro subscription';
        toast.error(message, {
          id: 'upgrade-required',
          duration: 6000,
        });
      }
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const usersApi = {
  me: () => api.get<{ success: boolean; data: User }>('/users/me').then(unwrap),
  get: (id: string) => api.get<{ success: boolean; data: User }>(`/users/${id}`).then(unwrap),
  list: () => api.get<{ success: boolean; data: User[] }>('/users').then(unwrap),
  create: (data: CreateUserInput) =>
    api.post<{ success: boolean; data: User }>('/users', data).then(unwrap),
  update: (id: string, data: UpdateUserProfileInput) =>
    api.put<{ success: boolean; data: User }>(`/users/${id}`, data).then(unwrap),
  preferences: {
    get: (id: string) =>
      api.get<{ success: boolean; data: UserPreferences }>(`/users/${id}/preferences`).then(unwrap),
    update: (id: string, data: Partial<UserPreferences>) =>
      api.put<{ success: boolean; data: UserPreferences }>(`/users/${id}/preferences`, data).then(unwrap),
  },
  changePassword: (id: string, data: { currentPassword: string; newPassword: string }) =>
    api.put(`/users/${id}/password`, data).then(unwrap),
};

// ---------------------------------------------------------------------------
// Time Entries
// ---------------------------------------------------------------------------

interface StartTimerRequest {
  projectId: string;
  description?: string;
  isBillable?: boolean;
  tags?: string[];
}

interface StopTimerRequest {
  description?: string;
}

export const timeEntriesApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: TimeEntry[] }>('/time-entries', { params }).then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: TimeEntry }>(`/time-entries/${id}`).then(unwrap),
  create: (data: CreateTimeEntryInput) =>
    api.post<{ success: boolean; data: TimeEntry }>('/time-entries', data).then(unwrap),
  update: (id: string, data: Partial<CreateTimeEntryInput>) =>
    api.put<{ success: boolean; data: TimeEntry }>(`/time-entries/${id}`, data).then(unwrap),
  delete: (id: string) =>
    api.delete<{ success: boolean; data: void }>(`/time-entries/${id}`).then(unwrap),
  submit: (ids: string[]) =>
    api.post('/time-entries/submit', { timeEntryIds: ids }).then(unwrap),
  approve: (ids: string[]) =>
    api.post('/time-entries/approve', { timeEntryIds: ids }).then(unwrap),
  reject: (ids: string[], reason: string) =>
    api.post('/time-entries/reject', { timeEntryIds: ids, rejectionReason: reason }).then(unwrap),
  withdraw: (ids: string[]) =>
    api.post('/time-entries/withdraw', { timeEntryIds: ids }).then(unwrap),
  dailySummary: (params: Record<string, string>) =>
    api.get('/time-entries/daily-summary', { params }).then(unwrap),
  weeklyOverview: (params: Record<string, string>) =>
    api.get('/time-entries/weekly-overview', { params }).then(unwrap),
  timer: {
    start: (data: StartTimerRequest) =>
      api.post('/time-entries/timer/start', data).then(unwrap),
    stop: (data?: StopTimerRequest) =>
      api.post('/time-entries/timer/stop', data).then(unwrap),
    active: () =>
      api.get('/time-entries/timer/active').then(unwrap),
  },
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export const projectsApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: Project[] }>('/projects', { params }).then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: Project }>(`/projects/${id}`).then(unwrap),
  create: (data: CreateProjectInput) =>
    api.post<{ success: boolean; data: Project }>('/projects', data).then(unwrap),
  update: (id: string, data: UpdateProjectInput) =>
    api.put<{ success: boolean; data: Project }>(`/projects/${id}`, data).then(unwrap),
  delete: (id: string) =>
    api.delete<{ success: boolean; data: void }>(`/projects/${id}`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export const clientsApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: Client[] }>('/clients', { params }).then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: Client }>(`/clients/${id}`).then(unwrap),
  create: (data: CreateClientInput) =>
    api.post<{ success: boolean; data: Client }>('/clients', data).then(unwrap),
  update: (id: string, data: UpdateClientInput) =>
    api.put<{ success: boolean; data: Client }>(`/clients/${id}`, data).then(unwrap),
  delete: (id: string) =>
    api.delete<{ success: boolean; data: void }>(`/clients/${id}`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export const tasksApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: Task[] }>('/tasks', { params }).then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: Task }>(`/tasks/${id}`).then(unwrap),
  create: (data: CreateTaskInput) =>
    api.post<{ success: boolean; data: Task }>('/tasks', data).then(unwrap),
  update: (id: string, data: UpdateTaskInput) =>
    api.put<{ success: boolean; data: Task }>(`/tasks/${id}`, data).then(unwrap),
  delete: (id: string) =>
    api.delete<{ success: boolean; data: void }>(`/tasks/${id}`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const invoicesApi = {
  list: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: Invoice[] }>('/invoices', { params }).then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: Invoice }>(`/invoices/${id}`).then(unwrap),
  create: (data: CreateInvoiceInput) =>
    api.post<{ success: boolean; data: Invoice }>('/invoices', data).then(unwrap),
  update: (id: string, data: Partial<CreateInvoiceInput>) =>
    api.put<{ success: boolean; data: Invoice }>(`/invoices/${id}`, data).then(unwrap),
  send: (id: string) =>
    api.post<{ success: boolean; data: Invoice }>(`/invoices/${id}/send`).then(unwrap),
  recordPayment: (id: string, data: { amount: number; paymentDate: string; paymentMethod: string; reference?: string; notes?: string }) =>
    api.post(`/invoices/${id}/payments`, data).then(unwrap),
};

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export const organizationsApi = {
  list: () =>
    api.get<{ success: boolean; data: Organization[] }>('/organizations').then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: Organization }>(`/organizations/${id}`).then(unwrap),
  create: (data: CreateOrganizationInput) =>
    api.post<{ success: boolean; data: Organization }>('/organizations', data).then(unwrap),
  update: (id: string, data: UpdateOrganizationInput) =>
    api.put<{ success: boolean; data: Organization }>(`/organizations/${id}`, data).then(unwrap),
};

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export const invitationsApi = {
  list: () =>
    api.get<{ success: boolean; data: UserInvitation[] }>('/invitations').then(unwrap),
  get: (id: string) =>
    api.get<{ success: boolean; data: UserInvitation }>(`/invitations/${id}`).then(unwrap),
  create: (data: CreateInvitationInput) =>
    api.post<{ success: boolean; data: UserInvitation }>('/invitations', data).then(unwrap),
  cancel: (id: string) =>
    api.post(`/invitations/${id}/cancel`).then(unwrap),
  resend: (id: string) =>
    api.post(`/invitations/${id}/resend`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Currencies
// ---------------------------------------------------------------------------

export const currenciesApi = {
  list: () =>
    api.get<{ success: boolean; data: CurrencyInfo[] }>('/currencies').then(unwrap),
  exchangeRates: (params?: Record<string, string>) =>
    api.get<{ success: boolean; data: ExchangeRate[] }>('/currencies/exchange-rates', { params }).then(unwrap),
  organizationCurrencies: (orgId: string) =>
    api.get<{ success: boolean; data: CurrencyInfo[] }>(`/organizations/${orgId}/currencies`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Platform Billing
// ---------------------------------------------------------------------------

export interface PlatformSubscription {
  planId: string;
  status: string;
  quantity?: number;
  trialEnd?: string;
  currentPeriodEnd?: string;
}

export interface StripeLink {
  url: string;
}

export const platformBillingApi = {
  getSubscription: () =>
    api.get<{ success: boolean; data: PlatformSubscription }>('/platform/billing/subscription').then(unwrap),
  createCheckoutSession: (data: { plan: 'monthly' | 'annual'; quantity: number }) =>
    api.post<{ success: boolean; data: StripeLink }>('/platform/billing/checkout-session', data).then(unwrap),
  createPortalSession: () =>
    api.post<{ success: boolean; data: StripeLink }>('/platform/billing/portal-session').then(unwrap),
  startTrial: () =>
    api.post<{ success: boolean; data: PlatformSubscription }>('/platform/billing/trial').then(unwrap),
};

// ---------------------------------------------------------------------------
// Billing – Stripe Connect
// ---------------------------------------------------------------------------

export interface StripeConnectAccount {
  stripeAccountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}

export interface BillingCustomerInput {
  email: string;
  name?: string;
  phone?: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface BillingInvoiceInput {
  customerId: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    unitAmount: number;
    priceId?: string;
  }>;
  currency?: string;
  dueDate?: string;
  daysUntilDue?: number;
  collectionMethod?: 'charge_automatically' | 'send_invoice';
  description?: string;
  footer?: string;
  autoAdvance?: boolean;
  metadata?: Record<string, string>;
}

export interface BillingInvoiceRecord {
  invoiceId: string;
  stripeInvoiceId: string;
  status: string;
  total: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

export interface BillingCustomerRecord {
  customerId: string;
  stripeCustomerId: string;
  email: string;
  name?: string;
}

export interface BillingAnalytics {
  accountStatus: string;
  onboardingCompleted: boolean;
  customers: { total: number };
  revenue: {
    last30Days: number;
    totalCollected: number;
    totalInvoiced: number;
    collectionRate: string;
  };
  invoices: {
    total: number;
    outstanding: number;
    outstandingAmount: number;
    overdue: number;
    overdueAmount: number;
  };
  subscriptions: {
    active: number;
    total: number;
    canceled: number;
    pastDue: number;
  };
  payments: {
    total: number;
    succeeded: number;
    recentCount: number;
  };
}

export const billingConnectApi = {
  getAccount: (orgId: string) =>
    api.get<{ success: boolean; data: StripeConnectAccount }>(`/billing/organizations/${orgId}/stripe-account`).then(unwrap),
  createAccount: (orgId: string) =>
    api.post<{ success: boolean; data: StripeConnectAccount }>(`/billing/organizations/${orgId}/stripe-account`).then(unwrap),
  createOnboardingLink: (orgId: string) =>
    api.post<{ success: boolean; data: StripeLink }>(`/billing/organizations/${orgId}/stripe-account/onboarding-link`).then(unwrap),
  createLoginLink: (orgId: string) =>
    api.post<{ success: boolean; data: StripeLink }>(`/billing/organizations/${orgId}/stripe-account/login-link`).then(unwrap),

  createCustomer: (data: BillingCustomerInput) =>
    api.post<{ success: boolean; data: BillingCustomerRecord }>('/billing/customers', data).then(unwrap),
  listCustomers: () =>
    api.get<{ success: boolean; data: BillingCustomerRecord[] }>('/billing/customers').then(unwrap),

  createInvoice: (data: BillingInvoiceInput) =>
    api.post<{ success: boolean; data: BillingInvoiceRecord }>('/billing/invoices', data).then(unwrap),
  listInvoices: () =>
    api.get<{ success: boolean; data: BillingInvoiceRecord[] }>('/billing/invoices').then(unwrap),
  sendInvoice: (invoiceId: string) =>
    api.post<{ success: boolean; data: BillingInvoiceRecord }>(`/billing/invoices/${invoiceId}/send`).then(unwrap),
  recordStripePayment: (invoiceId: string, data?: { paidOutOfBand?: boolean }) =>
    api.post(`/billing/invoices/${invoiceId}/record-payment`, data).then(unwrap),

  getDashboardAnalytics: () =>
    api.get<{ success: boolean; data: BillingAnalytics }>('/billing/reports/dashboard').then(unwrap),
};

// ---------------------------------------------------------------------------
// Integrations – Xero
// ---------------------------------------------------------------------------

export interface XeroConnectionStatus {
  connected: boolean;
  tenantName?: string;
  tenantId?: string;
}

export interface XeroConnectResponse {
  authorizationUrl: string;
  state: string;
}

export interface XeroSyncContact {
  xeroContactId: string;
  name: string;
  email: string | undefined;
  phone: string | undefined;
  existingClientId: string | undefined;
  matchType: 'exact_name' | 'exact_email' | 'none';
}

export interface XeroSyncResult {
  contacts: XeroSyncContact[];
  summary: {
    total: number;
    new: number;
    matched: number;
  };
}

export interface XeroPushInvoiceResult {
  xeroInvoiceId: string;
  xeroInvoiceNumber: string;
  status: string;
}

export const xeroApi = {
  getStatus: () =>
    api.get<{ success: boolean; data: XeroConnectionStatus }>('/integrations/xero/status').then(unwrap),
  connect: () =>
    api.post<{ success: boolean; data: XeroConnectResponse }>('/integrations/xero/connect').then(unwrap),
  callback: (data: { code: string; state: string }) =>
    api.post<{ success: boolean; data: XeroConnectionStatus }>('/integrations/xero/callback', data).then(unwrap),
  disconnect: () =>
    api.post<{ success: boolean; data: { connected: false } }>('/integrations/xero/disconnect').then(unwrap),
  syncClients: () =>
    api.post<{ success: boolean; data: XeroSyncResult }>('/integrations/xero/sync-clients').then(unwrap),
  pushInvoice: (data: { invoiceId: string; xeroContactId: string; accountCode?: string }) =>
    api.post<{ success: boolean; data: XeroPushInvoiceResult }>('/integrations/xero/push-invoice', data).then(unwrap),
};

// ---------------------------------------------------------------------------
// GDPR / Privacy
// ---------------------------------------------------------------------------

export interface GdprExportResponse {
  requestId: string;
  status: string;
}

export interface GdprDeletionResponse {
  requestId: string;
  status: string;
}

export interface GdprExportStatusResponse {
  requestId: string;
  status: string;
  createdAt: string;
}

export interface GdprDeletionStatusResponse {
  requestId: string;
  status: string;
  createdAt: string;
}

export const gdprApi = {
  requestExport: () =>
    api.post<{ success: boolean; data: GdprExportResponse }>('/gdpr/export').then(unwrap),
  getExportStatus: (id: string) =>
    api.get<{ success: boolean; data: GdprExportStatusResponse }>(`/gdpr/export/${id}`).then(unwrap),
  requestDeletion: (confirmation: string) =>
    api.post<{ success: boolean; data: GdprDeletionResponse }>('/gdpr/deletion', { confirmation }).then(unwrap),
  getDeletionStatus: (id: string) =>
    api.get<{ success: boolean; data: GdprDeletionStatusResponse }>(`/gdpr/deletion/${id}`).then(unwrap),
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export const healthApi = {
  check: () => api.get('/health').then(unwrap),
};
